import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel, generateInvoiceNumber, getApiErrorMessage } from "../lib/utils";
import { safeFilter, safeIncludes } from "../lib/safeUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { SATProductSearch, SATUnitSearch } from "../components/SATSearch";
import { Separator } from "../components/ui/separator";

// Catálogo de unidades SAT para auto-llenar descripción
const SAT_UNIDADES = {
  "H87": "Pieza",
  "E48": "Servicio",
  "ACT": "Actividad",
  "KGM": "Kilogramo",
  "LTR": "Litro",
  "MTR": "Metro",
  "MTK": "Metro cuadrado",
  "MTQ": "Metro cúbico",
  "XBX": "Caja",
  "XPK": "Paquete",
  "SET": "Conjunto",
  "HUR": "Hora",
  "DAY": "Día",
  "MON": "Mes",
  "ANN": "Año",
};
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Receipt,
  Plus,
  PlusCircle,
  MinusCircle,
  MoreVertical,
  DollarSign,
  CheckCircle,
  Check,
  Clock,
  AlertTriangle,
  CreditCard,
  Trash2,
  Download,
  Upload,
  FileText,
  User,
  Calendar,
  Eye,
  Search,
  X,
  Stamp,
  FileCode,
  Loader2,
  Mail,
  FileCheck,
  FileX,
} from "lucide-react";
import { PDFPreviewModal } from "../components/PDFPreviewModal";
import { ItemDescriptionField } from "../components/ItemDescriptionField";

const PAYMENT_METHODS = [
  { value: "transferencia", label: "Transferencia Bancaria" },
  { value: "efectivo", label: "Efectivo" },
  { value: "cheque", label: "Cheque" },
  { value: "tarjeta", label: "Tarjeta" },
];

// Catálogo SAT c_FormaPago para CFDI
const SAT_FORMAS_PAGO = [
  { value: "01", label: "01 - Efectivo" },
  { value: "02", label: "02 - Cheque nominativo" },
  { value: "03", label: "03 - Transferencia electrónica de fondos" },
  { value: "04", label: "04 - Tarjeta de crédito" },
  { value: "05", label: "05 - Monedero electrónico" },
  { value: "06", label: "06 - Dinero electrónico" },
  { value: "08", label: "08 - Vales de despensa" },
  { value: "12", label: "12 - Dación en pago" },
  { value: "13", label: "13 - Pago por subrogación" },
  { value: "14", label: "14 - Pago por consignación" },
  { value: "15", label: "15 - Condonación" },
  { value: "17", label: "17 - Compensación" },
  { value: "23", label: "23 - Novación" },
  { value: "24", label: "24 - Confusión" },
  { value: "25", label: "25 - Remisión de deuda" },
  { value: "26", label: "26 - Prescripción o caducidad" },
  { value: "27", label: "27 - A satisfacción del acreedor" },
  { value: "28", label: "28 - Tarjeta de débito" },
  { value: "29", label: "29 - Tarjeta de servicios" },
  { value: "30", label: "30 - Aplicación de anticipos" },
  { value: "31", label: "31 - Intermediario pagos" },
  { value: "99", label: "99 - Por definir" },
];

const MONEDAS = [
  { value: "MXN", label: "MXN - Peso Mexicano" },
  { value: "USD", label: "USD - Dólar Americano" },
  { value: "EUR", label: "EUR - Euro" },
];

const PAYMENT_TERMS_OPTIONS = [
  { value: "contado", label: "Contado" },
  { value: "15_dias", label: "15 días" },
  { value: "30_dias", label: "30 días" },
  { value: "45_dias", label: "45 días" },
  { value: "60_dias", label: "60 días" },
  { value: "90_dias", label: "90 días" },
];

const METODO_PAGO_OPTIONS = [
  { value: "PUE", label: "PUE - Pago en Una sola Exhibición" },
  { value: "PPD", label: "PPD - Pago en Parcialidades o Diferido" },
];

// Catálogo SAT c_UsoCFDI para CFDI 4.0
const USO_CFDI_OPTIONS = [
  { value: "G01", label: "G01 - Adquisición de mercancías" },
  { value: "G02", label: "G02 - Devoluciones, descuentos o bonificaciones" },
  { value: "G03", label: "G03 - Gastos en general" },
  { value: "I01", label: "I01 - Construcciones" },
  { value: "I02", label: "I02 - Mobiliario y equipo de oficina" },
  { value: "I03", label: "I03 - Equipo de transporte" },
  { value: "I04", label: "I04 - Equipo de cómputo" },
  { value: "I05", label: "I05 - Dados, troqueles, moldes, matrices" },
  { value: "I06", label: "I06 - Comunicaciones telefónicas" },
  { value: "I07", label: "I07 - Comunicaciones satelitales" },
  { value: "I08", label: "I08 - Otra maquinaria y equipo" },
  { value: "D01", label: "D01 - Honorarios médicos, dentales y hospitalarios" },
  { value: "D02", label: "D02 - Gastos médicos por incapacidad o discapacidad" },
  { value: "D03", label: "D03 - Gastos funerales" },
  { value: "D04", label: "D04 - Donativos" },
  { value: "D05", label: "D05 - Intereses por créditos hipotecarios" },
  { value: "D06", label: "D06 - Aportaciones voluntarias al SAR" },
  { value: "D07", label: "D07 - Primas por seguros de gastos médicos" },
  { value: "D08", label: "D08 - Gastos de transportación escolar obligatoria" },
  { value: "D09", label: "D09 - Depósitos en cuentas para el ahorro" },
  { value: "D10", label: "D10 - Pagos por servicios educativos" },
  { value: "S01", label: "S01 - Sin efectos fiscales" },
  { value: "CP01", label: "CP01 - Pagos" },
  { value: "CN01", label: "CN01 - Nómina" },
];

export const Invoices = () => {
  const { api, company } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [overdueData, setOverdueData] = useState({ overdue: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [creditNoteDialogOpen, setCreditNoteDialogOpen] = useState(false);
  const [cfdiDialogOpen, setCfdiDialogOpen] = useState(false);
  const [referenceDialogOpen, setReferenceDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [multiPaymentDialogOpen, setMultiPaymentDialogOpen] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportForm, setExportForm] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
    format: "excel"
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientStatement, setClientStatement] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [creditNotes, setCreditNotes] = useState([]);
  const [motivosNC, setMotivosNC] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [stamping, setStamping] = useState(false);
  const [stampingCreditNote, setStampingCreditNote] = useState(null);
  const [sendingCreditNoteEmail, setSendingCreditNoteEmail] = useState(null);
  const [cancellingCreditNote, setCancellingCreditNote] = useState(null);
  const [deletingCreditNote, setDeletingCreditNote] = useState(null);
  const [referenceForm, setReferenceForm] = useState({
    reference: "",
    custom_field: "",
    custom_field_label: "",
  });
  const [cfdiForm, setCfdiForm] = useState({
    xml_file: null,
    pdf_file: null,
  });
  
  const [formData, setFormData] = useState({
    client_id: "",
    project_id: "",
    invoice_number: "",
    reference: "",  // OC Cliente / Orden de Trabajo
    payment_terms: "contado",
    forma_pago: "99",  // SAT c_FormaPago - default: Por definir
    metodo_pago: "PUE",  // PUE = Pago en Una Exhibición, PPD = Pago en Parcialidades o Diferido
    uso_cfdi: "",  // Se cargará del cliente seleccionado
    items: [{ description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
    subtotal: 0,
    tax: 0,
    total: 0,
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "",
    custom_field: "",
    custom_field_label: "",
  });
  
  const [paymentForm, setPaymentForm] = useState({
    // Datos básicos del pago
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "transferencia",
    reference: "",
    notes: "",
    proof_file: null,
    // Datos SAT para complemento de pago CFDI
    sat_forma_pago: "03", // Default: Transferencia electrónica
    moneda_pago: "MXN",
    tipo_cambio: "1",
    num_operacion: "",
    // Datos bancarios ordenante (quien paga)
    rfc_banco_ordenante: "",
    nombre_banco_ordenante: "",
    cuenta_ordenante: "",
    // Datos bancarios beneficiario (quien recibe)
    rfc_banco_beneficiario: "",
    cuenta_beneficiaria: "",
    // Control de parcialidades
    num_parcialidad: "1",
    // Timbrado del complemento
    timbrar_complemento: false,
  });
  
  const [stampingPayment, setStampingPayment] = useState(false);
  
  // PDF Preview states
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewCreditNote, setPreviewCreditNote] = useState(null);
  const [previewPayment, setPreviewPayment] = useState(null);
  
  const [creditNoteForm, setCreditNoteForm] = useState({
    invoice_id: "",
    credit_note_number: "",
    issue_date: new Date().toISOString().split("T")[0],
    concept: "",
    reason: "",
    items: [{ description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
    subtotal: 0,
    tax: 0,
    total: 0,
    sat_tipo_relacion: "01",
    status: "applied",
  });

  // Estado para pago multi-factura
  const [multiPaymentForm, setMultiPaymentForm] = useState({
    client_id: "",
    total_amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "transferencia",
    reference: "",
    notes: "",
    sat_forma_pago: "03",
    moneda_pago: "MXN",
    tipo_cambio: "1",
    num_operacion: "",
    rfc_banco_ordenante: "",
    cuenta_ordenante: "",
    rfc_banco_beneficiario: "",
    cuenta_beneficiaria: "",
    invoice_payments: [], // { invoice_id, amount }
  });
  const [selectedClientInvoices, setSelectedClientInvoices] = useState([]);
  const [createdMultiPayments, setCreatedMultiPayments] = useState(null);
  
  const [searchFilter, setSearchFilter] = useState("");

  // Filter invoices based on search
  // Helper function to get project name
  const getProjectName = (projectId) => {
    if (!projectId) return "";
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "";
  };

  const getFilteredInvoices = (invoiceList) => {
    if (!searchFilter) return Array.isArray(invoiceList) ? invoiceList : [];
    const search = searchFilter.toLowerCase();
    
    return safeFilter(invoiceList, (inv) => {
      if (!inv) return false;
      // Get client name from clients array or from invoice directly
      const clientName = inv.client_name || getClientName(inv.client_id) || "";
      const projectName = getProjectName(inv.project_id) || "";
      
      // Search in main invoice fields
      const mainFieldsMatch = (
        safeIncludes(inv.invoice_number, search) ||
        safeIncludes(inv.concept, search) ||
        safeIncludes(clientName, search) ||
        safeIncludes(projectName, search) ||
        safeIncludes(inv.sat_uuid, search) ||
        safeIncludes(formatCurrency(inv.total), search) ||
        safeIncludes(inv.reference, search) ||
        safeIncludes(inv.purchase_order, search)
      );
      
      if (mainFieldsMatch) return true;
      
      // Search in items/concepts (description, clave_producto, clave_unidad, unit)
      const items = inv.items || [];
      const itemsMatch = items.some(item => 
        safeIncludes(item?.description, search) ||
        safeIncludes(item?.concept, search) ||
        safeIncludes(item?.clave_producto, search) ||
        safeIncludes(item?.clave_unidad, search) ||
        safeIncludes(item?.unit, search)
      );
      
      return itemsMatch;
    });
  };

  useEffect(() => {
    if (company?.id) {
      fetchData();
    }
  }, [company]);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes, projectsRes, paymentsRes, overdueRes, creditNotesRes, motivosRes, billingRes, bancosRes] = await Promise.all([
        api.get(`/invoices?company_id=${company.id}`).catch(e => { console.error("Error loading invoices:", e); return { data: [] }; }),
        api.get(`/clients?company_id=${company.id}`).catch(e => { console.error("Error loading clients:", e); return { data: [] }; }),
        api.get(`/projects?company_id=${company.id}`).catch(e => { console.error("Error loading projects:", e); return { data: [] }; }),
        api.get(`/payments?company_id=${company.id}`).catch(e => { console.error("Error loading payments:", e); return { data: [] }; }),
        api.get(`/invoices/overdue?company_id=${company.id}`).catch(e => { console.error("Error loading overdue:", e); return { data: { overdue: [], upcoming: [] } }; }),
        api.get(`/credit-notes?company_id=${company.id}`).catch(e => { console.error("Error loading credit notes:", e); return { data: [] }; }),
        api.get(`/sat/motivos-nota-credito`).catch(e => { console.error("Error loading motivos:", e); return { data: [] }; }),
        api.get(`/company/billing-status`).catch(() => ({ data: null })),
        api.get(`/sat/bancos`).catch(() => ({ data: [] })),
      ]);
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
      setPayments(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setOverdueData(overdueRes.data || { overdue: [], upcoming: [] });
      setCreditNotes(Array.isArray(creditNotesRes.data) ? creditNotesRes.data : []);
      setMotivosNC(Array.isArray(motivosRes.data) ? motivosRes.data : []);
      setBillingStatus(billingRes.data);
      setBancos(Array.isArray(bancosRes.data) ? bancosRes.data : []);
    } catch (error) {
      console.error("Error general al cargar datos:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return acc + (qty * price);
    }, 0);
    const tax = subtotal * 0.16;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === "quantity" || field === "unit_price") {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].total = qty * price;
    }
    // Auto-llenar campo "unit" cuando se selecciona clave_unidad SAT
    if (field === "clave_unidad" && value) {
      const claveUpper = value.toUpperCase().trim();
      if (SAT_UNIDADES[claveUpper]) {
        newItems[index].unit = SAT_UNIDADES[claveUpper];
      }
    }
    const { subtotal, tax, total } = calculateTotals(newItems);
    setFormData({ ...formData, items: newItems, subtotal, tax, total });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    const { subtotal, tax, total } = calculateTotals(newItems);
    setFormData({ ...formData, items: newItems, subtotal, tax, total });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { subtotal, tax, total } = calculateTotals(formData.items);
      const concept = formData.items.map(i => i.description).filter(Boolean).join(", ") || "Factura";
      
      const invoicePayload = {
        company_id: company.id,
        ...formData,
        concept,
        subtotal,
        tax,
        total,
        invoice_date: formData.invoice_date || new Date().toISOString().split("T")[0],
        due_date: formData.due_date || null,
      };
      
      let response;
      if (selectedInvoice) {
        // Editing existing invoice
        response = await api.put(`/invoices/${selectedInvoice.id}`, invoicePayload);
        toast.success("Factura actualizada");
      } else {
        // Creating new invoice
        response = await api.post("/invoices", invoicePayload);
        toast.success("Factura registrada");
        
        // Send email notification only for new invoices
        const client = clients.find(c => c.id === formData.client_id);
        if (client?.email) {
          try {
            await api.post("/send-document-email", {
              company_id: company.id,
              document_type: "invoice",
              document_id: response.data.id,
              recipient_email: client.email,
              recipient_name: client.trade_name || client.name,
            });
          } catch (emailError) {
            console.log("Email notification failed:", emailError);
          }
        }
      }
      
      setDialogOpen(false);
      resetForm();
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, selectedInvoice ? "Error al actualizar factura" : "Error al registrar factura"));
    }
  };

  // Credit Note Functions
  const handleCreditNoteItemChange = (index, field, value) => {
    const newItems = [...creditNoteForm.items];
    newItems[index][field] = value;
    if (field === "quantity" || field === "unit_price") {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    // Auto-llenar campo "unit" cuando se selecciona clave_unidad SAT
    if (field === "clave_unidad" && value) {
      const claveUpper = value.toUpperCase().trim();
      if (SAT_UNIDADES[claveUpper]) {
        newItems[index].unit = SAT_UNIDADES[claveUpper];
      }
    }
    const { subtotal, tax, total } = calculateTotals(newItems);
    setCreditNoteForm({ ...creditNoteForm, items: newItems, subtotal, tax, total });
  };

  const addCreditNoteItem = () => {
    setCreditNoteForm({
      ...creditNoteForm,
      items: [...creditNoteForm.items, { description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
    });
  };

  const removeCreditNoteItem = (index) => {
    if (creditNoteForm.items.length === 1) return;
    const newItems = creditNoteForm.items.filter((_, i) => i !== index);
    const { subtotal, tax, total } = calculateTotals(newItems);
    setCreditNoteForm({ ...creditNoteForm, items: newItems, subtotal, tax, total });
  };

  const openCreditNoteDialog = (invoice) => {
    setSelectedInvoice(invoice);
    const ncNumber = `NC-${new Date().getFullYear()}-${String(creditNotes.length + 1).padStart(3, '0')}`;
    const saldoPendiente = (invoice.total || 0) - (invoice.paid_amount || 0);
    
    setCreditNoteForm({
      invoice_id: invoice.id,
      credit_note_number: ncNumber,
      issue_date: new Date().toISOString().split("T")[0],
      concept: `Nota de crédito para factura ${invoice.invoice_number}`,
      reason: "",
      items: [{ 
        description: invoice.concept || "Ajuste según factura original", 
        quantity: 1, 
        unit: "pza", 
        unit_price: saldoPendiente, 
        total: saldoPendiente, 
        clave_prod_serv: "", 
        clave_unidad: "ACT" 
      }],
      subtotal: saldoPendiente / 1.16,
      tax: (saldoPendiente / 1.16) * 0.16,
      total: saldoPendiente,
      sat_tipo_relacion: "01",
      status: "applied",
    });
    setCreditNoteDialogOpen(true);
  };

  const handleCreditNoteSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      const client = clients.find(c => c.id === selectedInvoice.client_id);
      const concept = creditNoteForm.items.map(i => i.description).filter(Boolean).join(", ") || "Nota de crédito";
      
      await api.post("/credit-notes", {
        company_id: company.id,
        client_id: selectedInvoice.client_id,
        invoice_id: selectedInvoice.id,
        credit_note_number: creditNoteForm.credit_note_number,
        issue_date: creditNoteForm.issue_date,
        concept,
        reason: creditNoteForm.reason,
        items: creditNoteForm.items,
        subtotal: creditNoteForm.subtotal,
        tax: creditNoteForm.tax,
        total: creditNoteForm.total,
        sat_tipo_relacion: creditNoteForm.sat_tipo_relacion,
        sat_uuid_relacionado: selectedInvoice.sat_invoice_uuid || null,
        status: creditNoteForm.status,
      });
      
      toast.success("Nota de crédito registrada y aplicada");
      setCreditNoteDialogOpen(false);
      fetchData();
      
      // Send email notification
      if (client?.email) {
        try {
          await api.post("/send-document-email", {
            company_id: company.id,
            document_type: "credit_note",
            document_id: creditNoteForm.credit_note_number,
            recipient_email: client.email,
            recipient_name: client.name,
          });
        } catch (emailError) {
          console.log("Email notification failed:", emailError);
        }
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al registrar nota de crédito"));
    }
  };

  const handleStampCreditNote = async (noteId) => {
    if (!window.confirm("¿Estás seguro de timbrar esta nota de crédito? Esta acción no se puede deshacer.")) return;
    
    setStampingCreditNote(noteId);
    try {
      const response = await api.post(`/credit-notes/${noteId}/stamp`);
      if (response.data.success) {
        toast.success(response.data.message || "Nota de crédito timbrada exitosamente");
        if (response.data.email_sent) {
          toast.success("Correo enviado al cliente");
        }
        fetchData();
      } else {
        toast.error(response.data.message || "Error al timbrar nota de crédito");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al timbrar nota de crédito"));
    } finally {
      setStampingCreditNote(null);
    }
  };

  const handleSendCreditNoteEmail = async (noteId) => {
    setSendingCreditNoteEmail(noteId);
    try {
      const response = await api.post(`/credit-notes/${noteId}/send-email`);
      if (response.data.success) {
        toast.success("Correo enviado exitosamente");
      } else {
        toast.error(response.data.message || "Error al enviar correo");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al enviar correo"));
    } finally {
      setSendingCreditNoteEmail(null);
    }
  };

  const handleCancelCreditNoteCFDI = async (noteId) => {
    const note = creditNotes.find(n => n.id === noteId);
    if (!note) return;
    
    if (!window.confirm(`¿Estás seguro de cancelar el CFDI de la nota ${note.credit_note_number}?\n\nEsto enviará una solicitud de cancelación al SAT.`)) {
      return;
    }
    
    setCancellingCreditNote(noteId);
    try {
      const response = await api.delete(`/credit-notes/${noteId}`);
      if (response.data.success) {
        toast.success(response.data.message || "CFDI cancelado exitosamente");
        // Refresh credit notes
        const creditNotesRes = await api.get(`/credit-notes?company_id=${company.id}`);
        setCreditNotes(Array.isArray(creditNotesRes.data) ? creditNotesRes.data : []);
      } else {
        toast.error(response.data.message || "Error al cancelar CFDI");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al cancelar CFDI"));
    } finally {
      setCancellingCreditNote(null);
    }
  };

  const handleDeleteCreditNote = async (noteId) => {
    const note = creditNotes.find(n => n.id === noteId);
    if (!note) return;
    
    // Solo permitir eliminar si no está timbrada o si ya fue cancelada
    if (note.cfdi_uuid && note.cfdi_status !== 'cancelled') {
      toast.error("Primero debes cancelar el CFDI antes de eliminar la nota");
      return;
    }
    
    if (!window.confirm(`¿Estás seguro de ELIMINAR permanentemente la nota ${note.credit_note_number}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    setDeletingCreditNote(noteId);
    try {
      await api.delete(`/credit-notes/${noteId}/permanent`);
      toast.success("Nota de crédito eliminada permanentemente");
      // Refresh credit notes
      const creditNotesRes = await api.get(`/credit-notes?company_id=${company.id}`);
      setCreditNotes(Array.isArray(creditNotesRes.data) ? creditNotesRes.data : []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al eliminar nota de crédito"));
    } finally {
      setDeletingCreditNote(null);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      let proofBase64 = null;
      if (paymentForm.proof_file) {
        proofBase64 = await fileToBase64(paymentForm.proof_file);
      }
      
      // Calcular saldo anterior e insoluto
      const saldoAnterior = (selectedInvoice.total || 0) - (selectedInvoice.paid_amount || 0);
      const saldoInsoluto = saldoAnterior - parseFloat(paymentForm.amount);
      
      const paymentResponse = await api.post("/payments", {
        company_id: company.id,
        invoice_id: selectedInvoice.id,
        client_id: selectedInvoice.client_id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        notes: paymentForm.notes,
        proof_file: proofBase64,
        // Datos SAT para Complemento de Pago
        sat_forma_pago: paymentForm.sat_forma_pago,
        moneda_pago: paymentForm.moneda_pago,
        tipo_cambio: parseFloat(paymentForm.tipo_cambio) || 1,
        num_operacion: paymentForm.num_operacion,
        rfc_banco_ordenante: paymentForm.rfc_banco_ordenante,
        nombre_banco_ordenante: paymentForm.nombre_banco_ordenante,
        cuenta_ordenante: paymentForm.cuenta_ordenante,
        rfc_banco_beneficiario: paymentForm.rfc_banco_beneficiario,
        cuenta_beneficiaria: paymentForm.cuenta_beneficiaria,
        num_parcialidad: parseInt(paymentForm.num_parcialidad) || 1,
        saldo_anterior: saldoAnterior,
        saldo_insoluto: Math.max(0, saldoInsoluto),
      });
      
      toast.success("Abono registrado exitosamente");
      
      // If timbrar_complemento is checked and invoice is PPD, stamp the payment
      if (paymentForm.timbrar_complemento && selectedInvoice.metodo_pago === "PPD" && selectedInvoice.cfdi_status === "stamped") {
        const paymentId = paymentResponse.data.payment_id;
        if (paymentId) {
          setStampingPayment(true);
          try {
            const stampResponse = await api.post(`/payments/${paymentId}/stamp`);
            toast.success(`Complemento de pago timbrado: ${stampResponse.data.uuid}`);
          } catch (stampError) {
            toast.error(getApiErrorMessage(stampError, "Error al timbrar complemento de pago"));
          } finally {
            setStampingPayment(false);
          }
        }
      }
      
      setPaymentDialogOpen(false);
      resetPaymentForm();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al registrar abono"));
    }
  };

  const handleViewStatement = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/statement`);
      setClientStatement(response.data);
      setStatementDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar estado de cuenta");
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta factura?")) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success("Factura eliminada");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar factura");
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      toast.info("Generando PDF...");
      const response = await api.get(`/pdf/invoice/${invoiceId}`);
      const { filename, content } = response.data;
      
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };

  const handleDownloadStatementPDF = async (clientId) => {
    try {
      toast.info("Generando estado de cuenta PDF...");
      const response = await api.get(`/clients/${clientId}/statement/pdf`);
      const { filename, content } = response.data;
      
      const byteCharacters = atob(content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Estado de cuenta descargado");
    } catch (error) {
      toast.error("Error al generar estado de cuenta PDF");
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      project_id: "",
      invoice_number: "(Automático)",
      reference: "",
      payment_terms: "contado",
      items: [{ description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
      subtotal: 0,
      tax: 0,
      total: 0,
      invoice_date: new Date().toISOString().split("T")[0],
      due_date: "",
      custom_field: "",
      custom_field_label: "",
    });
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: "",
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: "transferencia",
      reference: "",
      notes: "",
      proof_file: null,
    });
    setSelectedInvoice(null);
  };

  const openNewInvoiceDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditReferenceDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setReferenceForm({
      reference: invoice.reference || "",
      custom_field: invoice.custom_field || "",
      custom_field_label: invoice.custom_field_label || "",
    });
    setReferenceDialogOpen(true);
  };

  const handleSaveReference = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      if (referenceForm.reference) params.append('reference', referenceForm.reference);
      if (referenceForm.custom_field) params.append('custom_field', referenceForm.custom_field);
      if (referenceForm.custom_field_label) params.append('custom_field_label', referenceForm.custom_field_label);
      
      await api.patch(`/invoices/${selectedInvoice.id}/reference?${params.toString()}`);
      toast.success("Referencia actualizada exitosamente");
      setReferenceDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar referencia: " + (error.response?.data?.detail || error.message));
    }
  };

  const openEditInvoiceDialog = (invoice) => {
    // Check if invoice is already stamped
    if (invoice.cfdi_status === "stamped") {
      toast.error("No se puede editar una factura ya timbrada");
      return;
    }
    
    // Normalize items to ensure numeric values
    const normalizedItems = (invoice.items || []).map(item => ({
      ...item,
      quantity: parseFloat(item.quantity) || 1,
      unit_price: parseFloat(item.unit_price) || 0,
      total: parseFloat(item.total) || 0,
    }));
    
    setSelectedInvoice(invoice);
    setFormData({
      client_id: invoice.client_id || "",
      project_id: invoice.project_id || "",
      invoice_number: invoice.invoice_number || "",
      reference: invoice.reference || "",
      payment_terms: invoice.payment_terms || "contado",
      forma_pago: invoice.forma_pago || "99",
      metodo_pago: invoice.metodo_pago || "PUE",
      uso_cfdi: invoice.uso_cfdi || "",
      items: normalizedItems.length > 0 
        ? normalizedItems 
        : [{ description: "", quantity: 1, unit: "pza", unit_price: 0, total: 0, clave_prod_serv: "", clave_unidad: "" }],
      subtotal: parseFloat(invoice.subtotal) || 0,
      tax: parseFloat(invoice.tax) || 0,
      total: parseFloat(invoice.total) || 0,
      invoice_date: invoice.invoice_date ? invoice.invoice_date.split("T")[0] : new Date().toISOString().split("T")[0],
      due_date: invoice.due_date ? invoice.due_date.split("T")[0] : "",
      custom_field: invoice.custom_field || "",
      custom_field_label: invoice.custom_field_label || "",
    });
    setDialogOpen(true);
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    
    // Calcular número de parcialidad automáticamente
    // Contar pagos existentes para esta factura
    const pagosFactura = payments.filter(p => p.invoice_id === invoice.id);
    const numParcialidad = pagosFactura.length + 1;
    
    setPaymentForm(prev => ({
      ...prev,
      amount: (invoice.total - invoice.paid_amount).toFixed(2),
      num_parcialidad: numParcialidad.toString(),
      // Resetear otros campos
      payment_date: new Date().toISOString().split("T")[0],
      sat_forma_pago: "03",
      moneda_pago: "MXN",
      tipo_cambio: "1",
      num_operacion: "",
      rfc_banco_ordenante: "",
      nombre_banco_ordenante: "",
      cuenta_ordenante: "",
      rfc_banco_beneficiario: "",
      cuenta_beneficiaria: "",
      reference: "",
      notes: "",
      proof_file: null,
    }));
    setPaymentDialogOpen(true);
  };

  // openSatDialog removed - SAT upload will be handled through Facturama integration

  const getClientName = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return "N/A";
    const displayName = client.trade_name || client.name;
    return client.reference ? `${displayName} (${client.reference})` : displayName;
  };

  const stats = {
    total: invoices.reduce((acc, inv) => acc + inv.total, 0),
    collected: invoices.reduce((acc, inv) => acc + inv.paid_amount, 0),
    pending: invoices.reduce((acc, inv) => acc + (inv.total - inv.paid_amount), 0),
    overdue: overdueData.overdue?.length || 0,
  };

  // ===== CFDI FUNCTIONS =====
  const handleStampInvoice = async (invoiceId) => {
    if (!billingStatus?.can_stamp) {
      toast.error(billingStatus?.message || "No puedes timbrar facturas");
      return;
    }
    
    try {
      setStamping(true);
      const response = await api.post(`/invoices/${invoiceId}/stamp`);
      if (response.data.success) {
        toast.success("Factura timbrada exitosamente");
        fetchData();
      } else {
        toast.error(response.data.message || "Error al timbrar");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al timbrar factura"));
    } finally {
      setStamping(false);
    }
  };

  const handleDownloadXML = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/cfdi/xml`);
      if (response.data.content) {
        const blob = new Blob([atob(response.data.content)], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'cfdi.xml';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Error al descargar XML");
    }
  };

  const handleDownloadCancellationAcuse = async (invoiceId, format = 'xml') => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/cancellation-acuse?format=${format}`, {
        responseType: 'blob'
      });
      
      const contentType = format === 'pdf' ? 'application/pdf' : 'application/xml';
      const extension = format === 'pdf' ? 'pdf' : 'xml';
      
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Acuse_Cancelacion_${invoiceId}.${extension}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Acuse de cancelación descargado (${format.toUpperCase()})`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "El acuse de cancelación no está disponible para esta factura";
      toast.error(errorMsg);
    }
  };

  const handleResendCancellationNotification = async (invoiceId) => {
    try {
      const response = await api.post(`/invoices/${invoiceId}/resend-cancellation-notification`);
      if (response.data.success) {
        toast.success(response.data.message || "Notificación enviada exitosamente");
      } else {
        toast.error(response.data.message || "Error al enviar notificación");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al enviar notificación de cancelación";
      toast.error(errorMsg);
    }
  };

  const handleDownloadCFDIPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/cfdi/pdf`);
      if (response.data.content) {
        const blob = new Blob([Uint8Array.from(atob(response.data.content), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'cfdi.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error("Error al descargar PDF del CFDI");
    }
  };

  const handleDownloadPaymentXML = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/complement/xml`);
      if (response.data.content) {
        const blob = new Blob([Uint8Array.from(atob(response.data.content), c => c.charCodeAt(0))], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'complemento_pago.xml';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("XML del complemento descargado");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al descargar XML del complemento"));
    }
  };

  // Download Credit Note XML
  const handleDownloadCreditNoteXML = async (noteId) => {
    try {
      const response = await api.get(`/credit-notes/${noteId}/xml`);
      if (response.data.content) {
        const blob = new Blob([Uint8Array.from(atob(response.data.content), c => c.charCodeAt(0))], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'nota_credito.xml';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("XML de nota de crédito descargado");
      } else {
        toast.error("XML no disponible para esta nota de crédito");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al descargar XML de nota de crédito"));
    }
  };

  const handleDownloadPaymentPDF = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/complement/pdf`);
      if (response.data.content) {
        const blob = new Blob([Uint8Array.from(atob(response.data.content), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || 'complemento_pago.pdf';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("PDF del complemento descargado");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al descargar PDF del complemento"));
    }
  };

  const handleDownloadPaymentCancellationAcuse = async (paymentId, repFolio, format = 'xml') => {
    try {
      const response = await api.get(`/payments/${paymentId}/complement/cancellation-acuse?format=${format}`);
      if (response.data.content) {
        const contentType = format === 'pdf' ? 'application/pdf' : 'application/xml';
        const blob = new Blob([Uint8Array.from(atob(response.data.content), c => c.charCodeAt(0))], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename || `Acuse_Cancelacion_${repFolio || paymentId}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Acuse de cancelación (${format.toUpperCase()}) descargado`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "El acuse de cancelación no está disponible";
      toast.error(errorMsg);
    }
  };

  const handleStampPayment = async (paymentId) => {
    if (!window.confirm("¿Timbrar este pago como Complemento de Pago (REP)?")) {
      return;
    }
    
    try {
      const response = await api.post(`/payments/${paymentId}/stamp`);
      toast.success(`Complemento timbrado: ${response.data.uuid}`);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al timbrar complemento de pago"));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("¿Estás seguro de eliminar este pago? Se actualizará el saldo de la factura.")) {
      return;
    }
    
    try {
      await api.delete(`/payments/${paymentId}`);
      toast.success("Pago eliminado correctamente");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al eliminar pago"));
    }
  };

  const handleCancelPaymentComplement = async (paymentId, repFolio) => {
    if (!window.confirm(`¿Estás seguro de cancelar el complemento de pago ${repFolio || ''}?\n\nEsta acción cancelará el CFDI ante el SAT y revertirá el pago aplicado a la factura.`)) {
      return;
    }
    
    try {
      const response = await api.post(`/payments/${paymentId}/cancel-cfdi`);
      if (response.data.success) {
        toast.success(response.data.message || "Complemento de pago cancelado");
        fetchData();
      } else {
        toast.error(response.data.message || "Error al cancelar");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al cancelar complemento de pago"));
    }
  };

  // ============== MULTI-INVOICE PAYMENT ==============
  const openMultiPaymentDialog = () => {
    setMultiPaymentForm({
      client_id: "",
      total_amount: "",
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: "transferencia",
      reference: "",
      notes: "",
      sat_forma_pago: "03",
      moneda_pago: "MXN",
      tipo_cambio: "1",
      num_operacion: "",
      rfc_banco_ordenante: "",
      cuenta_ordenante: "",
      rfc_banco_beneficiario: "",
      cuenta_beneficiaria: "",
      invoice_payments: [],
    });
    setSelectedClientInvoices([]);
    setCreatedMultiPayments(null);
    setMultiPaymentDialogOpen(true);
  };

  const handleMultiPaymentClientChange = async (clientId) => {
    setMultiPaymentForm({ ...multiPaymentForm, client_id: clientId, invoice_payments: [] });
    
    if (!clientId) {
      setSelectedClientInvoices([]);
      return;
    }
    
    // Facturas PPD timbradas con saldo pendiente
    const pendingInvoices = invoices.filter(inv => 
      inv.client_id === clientId && 
      inv.metodo_pago === "PPD" && 
      inv.cfdi_status === "stamped" &&
      inv.status !== "cancelled" &&
      (inv.total - (inv.paid_amount || 0)) > 0.01
    );

    // Facturas marcadas como pagadas pero SIN registro de pago (no pueden timbrar su REP)
    const paidWithoutPayment = invoices.filter(inv =>
      inv.client_id === clientId &&
      inv.metodo_pago === "PPD" &&
      inv.cfdi_status === "stamped" &&
      inv.status !== "cancelled" &&
      (inv.total - (inv.paid_amount || 0)) <= 0.01 &&
      !payments.some(p => p.invoice_id === inv.id)
    );

    const selectableInvoices = [...pendingInvoices, ...paidWithoutPayment];

    setSelectedClientInvoices(selectableInvoices);

    // Initialize invoice_payments with 0 amounts
    setMultiPaymentForm(prev => ({
      ...prev,
      client_id: clientId,
      invoice_payments: selectableInvoices.map(inv => ({
        invoice_id: inv.id,
        amount: 0,
        balance: Math.max(inv.total - (inv.paid_amount || 0), 0.01) > 0.01
          ? inv.total - (inv.paid_amount || 0)
          : inv.total
      }))
    }));
  };

  const handleInvoiceAmountChange = (invoiceId, amount) => {
    setMultiPaymentForm(prev => ({
      ...prev,
      invoice_payments: prev.invoice_payments.map(ip =>
        ip.invoice_id === invoiceId ? { ...ip, amount: parseFloat(amount) || 0 } : ip
      )
    }));
  };

  const getMultiPaymentDistributedTotal = () => {
    return multiPaymentForm.invoice_payments.reduce((sum, ip) => sum + (parseFloat(ip.amount) || 0), 0);
  };

  const handleCreateMultiPayment = async () => {
    // Validations
    if (!multiPaymentForm.client_id) {
      toast.error("Selecciona un cliente");
      return;
    }
    
    const totalAmount = parseFloat(multiPaymentForm.total_amount) || 0;
    if (totalAmount <= 0) {
      toast.error("Ingresa el monto total del pago");
      return;
    }
    
    const distributedTotal = getMultiPaymentDistributedTotal();
    if (Math.abs(distributedTotal - totalAmount) > 0.01) {
      toast.error(`La distribución ($${distributedTotal.toFixed(2)}) no coincide con el monto total ($${totalAmount.toFixed(2)})`);
      return;
    }
    
    const paymentsWithAmount = multiPaymentForm.invoice_payments.filter(ip => ip.amount > 0);
    if (paymentsWithAmount.length === 0) {
      toast.error("Distribuye el pago en al menos una factura");
      return;
    }
    
    try {
      const response = await api.post("/payments/multi-invoice", {
        company_id: company.id,
        client_id: multiPaymentForm.client_id,
        total_amount: totalAmount,
        payment_date: multiPaymentForm.payment_date,
        payment_method: multiPaymentForm.payment_method,
        reference: multiPaymentForm.reference,
        notes: multiPaymentForm.notes,
        sat_forma_pago: multiPaymentForm.sat_forma_pago,
        moneda_pago: multiPaymentForm.moneda_pago,
        tipo_cambio: parseFloat(multiPaymentForm.tipo_cambio) || 1,
        num_operacion: multiPaymentForm.num_operacion,
        rfc_banco_ordenante: multiPaymentForm.rfc_banco_ordenante || null,
        cuenta_ordenante: multiPaymentForm.cuenta_ordenante || null,
        rfc_banco_beneficiario: multiPaymentForm.rfc_banco_beneficiario || null,
        cuenta_beneficiaria: multiPaymentForm.cuenta_beneficiaria || null,
        invoice_payments: paymentsWithAmount.map(ip => ({
          invoice_id: ip.invoice_id,
          amount: ip.amount
        }))
      });
      
      toast.success(response.data.message);
      // Store both the payments array AND the payment_group_id
      setCreatedMultiPayments({
        payments: response.data.payments,
        payment_group_id: response.data.payment_group_id
      });
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al crear pago"));
    }
  };

  const handleStampMultiPayments = async () => {
    if (!createdMultiPayments || !createdMultiPayments.payments || createdMultiPayments.payments.length === 0) return;
    
    try {
      // Use the payment_group_id that was returned when creating the multi-invoice payment
      const paymentGroupId = createdMultiPayments.payment_group_id;
      
      if (paymentGroupId) {
        // Use the consolidated endpoint - ONE single REP for all invoices
        const response = await api.post(`/payments/group/${paymentGroupId}/stamp`);
        toast.success(response.data.message || "Complemento de pago consolidado timbrado exitosamente");
      } else {
        // Fallback to individual stamping if no group found (shouldn't happen)
        toast.error("Error: No se encontró el grupo de pagos. Contacte soporte.");
        return;
      }
      
      fetchData();
      setMultiPaymentDialogOpen(false);
      setCreatedMultiPayments(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al timbrar complemento consolidado"));
    }
  };

  const handleCancelCFDI = async (invoiceId) => {
    if (!window.confirm("¿Estás seguro de cancelar este CFDI? Esta acción puede tener implicaciones fiscales.")) {
      return;
    }
    
    try {
      const response = await api.post(`/invoices/${invoiceId}/cancel-cfdi`);
      if (response.data.success) {
        toast.success("CFDI cancelado");
        fetchData();
      } else {
        toast.error(response.data.message || "Error al cancelar");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al cancelar CFDI"));
    }
  };

  // Export functions for ASPEL, CONTPAQi, Excel, XML
  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const endpoint = format === "xml" ? "/invoices/export/xml-zip" : `/invoices/export/${format}`;
      const response = await api.post(endpoint, {
        start_date: exportForm.start_date,
        end_date: exportForm.end_date,
        format: format
      }, {
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `CFDIs_${exportForm.start_date}_a_${exportForm.end_date}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      } else {
        // Add extension based on format
        const extensions = { excel: '.xlsx', aspel: '.txt', contpaqi: '.csv', xml: '.zip' };
        filename += extensions[format] || '';
      }
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Archivo ${format.toUpperCase()} descargado`);
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al exportar CFDIs"));
    } finally {
      setExportLoading(false);
    }
  };

  const [sendingEmail, setSendingEmail] = useState(null);
  
  const handleSendInvoiceEmail = async (invoiceId, customEmail = null) => {
    setSendingEmail(invoiceId);
    try {
      const payload = customEmail ? { email: customEmail } : {};
      const response = await api.post(`/invoices/${invoiceId}/send-email`, payload);
      if (response.data.success) {
        toast.success("Factura enviada por correo electrónico");
        fetchData();
      } else {
        toast.error(response.data.message || "Error al enviar factura");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al enviar factura por correo"));
    } finally {
      setSendingEmail(null);
    }
  };


  const openCfdiDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setCfdiForm({ xml_file: null, pdf_file: null });
    setCfdiDialogOpen(true);
  };

  const handleUploadManualCFDI = async (e) => {
    e.preventDefault();
    
    // Validar que ambos archivos estén presentes
    if (!cfdiForm.xml_file) {
      toast.error("El archivo XML es obligatorio");
      return;
    }
    if (!cfdiForm.pdf_file) {
      toast.error("El archivo PDF es obligatorio");
      return;
    }
    
    try {
      const xmlContent = await fileToBase64(cfdiForm.xml_file);
      const pdfContent = await fileToBase64(cfdiForm.pdf_file);
      
      await api.post(`/invoices/${selectedInvoice.id}/upload-cfdi`, {
        xml_content: xmlContent,
        pdf_content: pdfContent,
      });
      
      toast.success("CFDI vinculado correctamente. UUID extraído del XML.");
      setCfdiDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al subir CFDI"));
    }
  };

  const baseFilteredInvoices = activeTab === "all" 
    ? safeFilter(invoices, inv => inv && inv.status !== "cancelled" && inv.cfdi_status !== "cancellation_pending" && inv.cfdi_status !== "cancelled")
    : activeTab === "overdue"
    ? (overdueData.overdue || [])
    : activeTab === "upcoming"
    ? (overdueData.upcoming || [])
    : activeTab === "cancellation_pending"
    ? safeFilter(invoices, inv => inv && inv.cfdi_status === "cancellation_pending")
    : activeTab === "cancelled"
    ? safeFilter(invoices, inv => inv && (inv.status === "cancelled" || inv.cfdi_status === "cancelled"))
    : safeFilter(invoices, inv => inv && inv.status === activeTab);
  
  const filteredInvoices = getFilteredInvoices(baseFilteredInvoices);

  // Filtra la tabla de Complementos de Pago con el mismo buscador
  const filteredPayments = (() => {
    const list = Array.isArray(payments) ? payments : [];
    if (!searchFilter) return list;
    const search = searchFilter.toLowerCase();
    return list.filter(p => {
      const invoice = invoices.find(i => i.id === p.invoice_id);
      const client = clients.find(c => c.id === p.client_id);
      return (
        safeIncludes(invoice?.invoice_number, search) ||
        safeIncludes(client?.trade_name || client?.name, search) ||
        safeIncludes(p.rep_folio, search) ||
        safeIncludes(p.reference, search) ||
        safeIncludes(p.num_operacion, search) ||
        safeIncludes(p.cfdi_complemento_uuid, search) ||
        safeIncludes(formatCurrency(p.amount), search)
      );
    });
  })();

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-[Chivo] text-slate-900">Control de Facturación</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Gestión de facturas y cobranza</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-cfdis-btn">
                <Download className="mr-1 sm:mr-2 h-4 w-4" />
                Exportar CFDIs
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                <FileText className="mr-2 h-4 w-4" />
                Excel (Detallado)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setExportForm({...exportForm, format: 'aspel'}); setExportDialogOpen(true); }}>
                <FileCode className="mr-2 h-4 w-4" />
                ASPEL (TXT)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setExportForm({...exportForm, format: 'contpaqi'}); setExportDialogOpen(true); }}>
                <FileCode className="mr-2 h-4 w-4" />
                CONTPAQi (CSV)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { setExportForm({...exportForm, format: 'xml'}); setExportDialogOpen(true); }}>
                <Download className="mr-2 h-4 w-4" />
                XMLs (ZIP)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button className="btn-industrial" size="sm" onClick={openNewInvoiceDialog} data-testid="new-invoice-btn">
            <Plus className="mr-1 sm:mr-2 h-4 w-4" />
            Nueva Factura
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Facturado</p>
                <p className="text-lg sm:text-2xl font-bold">{formatCurrency(stats.total)}</p>
              </div>
              <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Cobrado</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-600">{formatCurrency(stats.collected)}</p>
              </div>
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Por Cobrar</p>
                <p className="text-lg sm:text-2xl font-bold text-amber-600">{formatCurrency(stats.pending)}</p>
              </div>
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500/50" />
            </div>
            <Progress value={(stats.collected / stats.total) * 100 || 0} className="mt-2" />
          </CardContent>
        </Card>
        <Card className={stats.overdue > 0 ? "border-red-200 bg-red-50" : ""}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Vencidas</p>
                <p className={`text-lg sm:text-2xl font-bold ${stats.overdue > 0 ? "text-red-600" : ""}`}>
                  {stats.overdue}
                </p>
              </div>
              <AlertTriangle className={`h-6 w-6 sm:h-8 sm:w-8 ${stats.overdue > 0 ? "text-red-500" : "text-slate-300"}`} />
            </div>
            {overdueData.total_overdue_amount > 0 && (
              <p className="text-xs text-red-600 mt-1">
                {formatCurrency(overdueData.total_overdue_amount)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Overdue Alert */}
      {overdueData.overdue?.length > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 hidden sm:block" />
              <div className="flex-1">
                <p className="font-semibold text-red-800 text-sm sm:text-base">¡Facturas Vencidas!</p>
                <p className="text-xs sm:text-sm text-red-700">
                  {overdueData.overdue.length} factura(s) - {formatCurrency(overdueData.total_overdue_amount)}
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-red-300 text-red-700 hover:bg-red-100 text-xs sm:text-sm"
                onClick={() => setActiveTab("overdue")}
              >
                Ver Vencidas
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto overflow-x-auto flex-nowrap">
          <TabsTrigger value="all" className="text-xs sm:text-sm">Todas ({safeFilter(invoices, inv => inv && inv.status !== "cancelled" && inv.cfdi_status !== "cancellation_pending" && inv.cfdi_status !== "cancelled").length})</TabsTrigger>
          <TabsTrigger value="pending" className="text-xs sm:text-sm">Pendientes</TabsTrigger>
          <TabsTrigger value="partial" className="text-xs sm:text-sm">Parciales</TabsTrigger>
          <TabsTrigger value="paid" className="text-xs sm:text-sm">Pagadas</TabsTrigger>
          <TabsTrigger value="overdue" className="text-red-600">
            Vencidas ({overdueData.overdue?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="text-amber-600">
            Próx. Vencer ({overdueData.upcoming?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="cancellation_pending" className="text-orange-600 text-xs sm:text-sm">
            En Cancelación ({safeFilter(invoices, inv => inv && inv.cfdi_status === "cancellation_pending").length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="text-slate-500 text-xs sm:text-sm">
            Canceladas ({safeFilter(invoices, inv => inv && (inv.status === "cancelled" || inv.cfdi_status === "cancelled")).length})
          </TabsTrigger>
          <TabsTrigger value="credit_notes" className="text-violet-600 text-xs sm:text-sm">
            N. Crédito ({creditNotes.length})
          </TabsTrigger>
          <TabsTrigger value="payments" className="text-emerald-600 text-xs sm:text-sm">
            Pagos ({payments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card data-testid="invoices-table-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                {activeTab === "payments" ? "Buscar en Complementos de Pago" : "Listado de Facturas"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search Filter */}
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por folio, cliente, concepto, UUID SAT..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="pl-9"
                    data-testid="invoices-search-filter"
                  />
                  {searchFilter && (
                    <button
                      onClick={() => setSearchFilter("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {searchFilter && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Filtro activo: "{searchFilter}"
                  </Badge>
                )}
              </div>
              <div className={`rounded-sm border overflow-x-auto ${activeTab === "payments" ? "hidden" : ""}`}>
                <Table className="min-w-[1200px]">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[120px]">Folio</TableHead>
                      <TableHead className="w-[150px]">Cliente</TableHead>
                      <TableHead className="w-[200px]">Concepto</TableHead>
                      <TableHead className="w-[100px]">Fecha</TableHead>
                      <TableHead className="w-[100px] text-right">Total</TableHead>
                      <TableHead className="w-[100px] text-right">Pagado</TableHead>
                      <TableHead className="w-[100px] text-right">Saldo</TableHead>
                      <TableHead className="w-[110px]">Vencimiento</TableHead>
                      <TableHead className="w-[130px]">Estado</TableHead>
                      <TableHead className="w-[140px] sticky right-0 bg-slate-50">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No hay facturas
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvoices.map((invoice) => (
                        <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                          <TableCell className="font-mono text-sm">
                            {invoice.invoice_number}
                            {invoice.sat_invoice_uuid && (
                              <Badge variant="outline" className="ml-2 text-xs">SAT</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="link" 
                              className="p-0 h-auto"
                              onClick={() => handleViewStatement(invoice.client_id)}
                            >
                              {invoice.client_name || getClientName(invoice.client_id)}
                            </Button>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{invoice.concept}</TableCell>
                          <TableCell className="text-slate-600">
                            {invoice.invoice_date ? formatDate(invoice.invoice_date) : formatDate(invoice.created_at)}
                          </TableCell>
                          <TableCell className="font-medium">{formatCurrency(invoice.total)}</TableCell>
                          <TableCell className="text-emerald-600">{formatCurrency(invoice.paid_amount)}</TableCell>
                          <TableCell className="text-amber-600">
                            {formatCurrency(invoice.total - invoice.paid_amount)}
                          </TableCell>
                          <TableCell>
                            {invoice.due_date ? (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span className={invoice.days_overdue > 0 ? "text-red-600 font-medium" : ""}>
                                  {formatDate(invoice.due_date)}
                                </span>
                                {invoice.days_overdue > 0 && (
                                  <Badge variant="destructive" className="ml-1 text-xs">
                                    +{invoice.days_overdue}d
                                  </Badge>
                                )}
                              </div>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            {/* Badge inteligente combinado */}
                            {(() => {
                              // Prioridad: Cancelado > Cancelación Pendiente > Timbrado+Pagado > Timbrado+Pendiente > Sin Timbrar
                              if (invoice.cfdi_status === "cancelled") {
                                return (
                                  <Badge className="bg-red-100 text-red-700 border border-red-300">
                                    CFDI Cancelado
                                  </Badge>
                                );
                              }
                              if (invoice.cfdi_status === "cancellation_pending") {
                                return (
                                  <Badge className="bg-amber-100 text-amber-700 border border-amber-300 animate-pulse">
                                    Cancelación Pendiente
                                  </Badge>
                                );
                              }
                              if (invoice.cfdi_status === "stamped") {
                                if (invoice.status === "paid") {
                                  return (
                                    <Badge className="bg-green-100 text-green-700 border border-green-300">
                                      Pagada ✓
                                    </Badge>
                                  );
                                }
                                return (
                                  <Badge className="bg-yellow-100 text-yellow-700 border border-yellow-300">
                                    Por Cobrar
                                  </Badge>
                                );
                              }
                              // Sin timbrar
                              return (
                                <Badge className="bg-gray-100 text-gray-600 border border-gray-300">
                                  Sin Timbrar
                                </Badge>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="sticky right-0 bg-white border-l">
                            <div className="flex items-center gap-1">
                              {/* Botones rápidos de descarga CFDI */}
                              {invoice.cfdi_status === "stamped" && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => handleDownloadXML(invoice.id)}
                                    title="Descargar XML"
                                  >
                                    <FileCode className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDownloadPDF(invoice.id)}
                                    title="Descargar PDF con Sellos"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => handleSendInvoiceEmail(invoice.id)}
                                    disabled={sendingEmail === invoice.id}
                                    title="Enviar por Correo"
                                  >
                                    {sendingEmail === invoice.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Mail className="h-4 w-4" />
                                    )}
                                  </Button>
                                </>
                              )}
                              
                              {/* Botones rápidos para facturas CANCELADAS */}
                              {invoice.cfdi_status === "cancelled" && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => handleDownloadXML(invoice.id)}
                                    title="Descargar XML Original"
                                  >
                                    <FileCode className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDownloadPDF(invoice.id)}
                                    title="Descargar PDF (Cancelado)"
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                    onClick={() => handleDownloadCancellationAcuse(invoice.id, 'xml')}
                                    title="Descargar Acuse Cancelación SAT (XML)"
                                  >
                                    <FileX className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setPreviewInvoice(invoice)}>
                                  <Eye className="mr-2 h-4 w-4 text-purple-500" />
                                  Vista Previa PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDownloadPDF(invoice.id)}>
                                  <Download className="mr-2 h-4 w-4 text-blue-500" />
                                  Descargar PDF
                                </DropdownMenuItem>
                                
                                {/* Edit option - only if not stamped */}
                                {invoice.cfdi_status !== "stamped" && (
                                  <DropdownMenuItem onClick={() => openEditInvoiceDialog(invoice)}>
                                    <FileText className="mr-2 h-4 w-4 text-amber-500" />
                                    Editar Factura
                                  </DropdownMenuItem>
                                )}
                                
                                {/* CFDI Options */}
                                {invoice.cfdi_status !== "stamped" && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleStampInvoice(invoice.id)} disabled={stamping}>
                                      <Stamp className="mr-2 h-4 w-4 text-green-500" />
                                      {stamping ? "Timbrando..." : "Timbrar CFDI"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openCfdiDialog(invoice)}>
                                      <Upload className="mr-2 h-4 w-4 text-orange-500" />
                                      Subir CFDI Manual
                                    </DropdownMenuItem>
                                  </>
                                )}
                                
                                {invoice.cfdi_status === "stamped" && (
                                  <>
                                    <DropdownMenuItem onClick={() => openEditReferenceDialog(invoice)}>
                                      <FileText className="mr-2 h-4 w-4 text-amber-500" />
                                      Editar Referencia/OC
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadXML(invoice.id)}>
                                      <FileCode className="mr-2 h-4 w-4 text-orange-500" />
                                      Descargar XML
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleSendInvoiceEmail(invoice.id)}
                                      disabled={sendingEmail === invoice.id}
                                    >
                                      {sendingEmail === invoice.id ? (
                                        <Loader2 className="mr-2 h-4 w-4 text-blue-500 animate-spin" />
                                      ) : (
                                        <Mail className="mr-2 h-4 w-4 text-blue-500" />
                                      )}
                                      Enviar por Correo
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleCancelCFDI(invoice.id)} className="text-red-600">
                                      <X className="mr-2 h-4 w-4" />
                                      Cancelar CFDI
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                
                                {invoice.cfdi_status === "cancellation_pending" && (
                                  <>
                                    <DropdownMenuItem disabled>
                                      <Loader2 className="mr-2 h-4 w-4 text-amber-500 animate-spin" />
                                      Cancelación en proceso...
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadPDF(invoice.id)}>
                                      <Download className="mr-2 h-4 w-4 text-gray-500" />
                                      Descargar PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadXML(invoice.id)}>
                                      <FileCode className="mr-2 h-4 w-4 text-orange-500" />
                                      Descargar XML
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadCancellationAcuse(invoice.id, 'xml')}>
                                      <FileText className="mr-2 h-4 w-4 text-red-500" />
                                      Descargar Acuse Solicitud (XML)
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleResendCancellationNotification(invoice.id)}>
                                      <Mail className="mr-2 h-4 w-4 text-blue-500" />
                                      Reenviar Notificación al Cliente
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                
                                {invoice.cfdi_status === "cancelled" && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleDownloadPDF(invoice.id)}>
                                      <Download className="mr-2 h-4 w-4 text-red-500" />
                                      Descargar PDF (Cancelado)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadXML(invoice.id)}>
                                      <FileCode className="mr-2 h-4 w-4 text-orange-500" />
                                      Descargar XML Original
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadCancellationAcuse(invoice.id, 'xml')}>
                                      <FileText className="mr-2 h-4 w-4 text-red-600" />
                                      Descargar Acuse Cancelación (XML)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDownloadCancellationAcuse(invoice.id, 'pdf')}>
                                      <FileText className="mr-2 h-4 w-4 text-red-600" />
                                      Descargar Acuse Cancelación (PDF)
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                
                                <DropdownMenuItem onClick={() => openPaymentDialog(invoice)}>
                                  <CreditCard className="mr-2 h-4 w-4 text-emerald-500" />
                                  Registrar Abono
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openCreditNoteDialog(invoice)}>
                                  <FileText className="mr-2 h-4 w-4 text-violet-500" />
                                  Nota de Crédito
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleViewStatement(invoice.client_id)}>
                                  <User className="mr-2 h-4 w-4 text-purple-500" />
                                  Estado de Cuenta
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(invoice.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Eliminar
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Credit Notes Tab */}
        <TabsContent value="credit_notes" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-violet-700">
                <FileText className="h-5 w-5" />
                Notas de Crédito
              </CardTitle>
              <CardDescription>Historial de notas de crédito emitidas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Folio NC</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Factura Rel.</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Concepto</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>UUID CFDI</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {creditNotes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No hay notas de crédito registradas
                        </TableCell>
                      </TableRow>
                    ) : (
                      creditNotes.map((nc) => {
                        const client = clients.find(c => c.id === nc.client_id);
                        const invoice = invoices.find(i => i.id === nc.invoice_id);
                        return (
                          <TableRow key={nc.id}>
                            <TableCell className="font-medium text-violet-700">{nc.credit_note_number}</TableCell>
                            <TableCell>{formatDate(nc.issue_date || nc.created_at)}</TableCell>
                            <TableCell>{invoice?.invoice_number || '-'}</TableCell>
                            <TableCell>{client?.name || '-'}</TableCell>
                            <TableCell className="max-w-xs truncate">{nc.concept}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(nc.total)}</TableCell>
                            <TableCell>
                              <Badge variant={nc.status === 'applied' ? 'default' : nc.status === 'cancelled' ? 'destructive' : 'secondary'}>
                                {nc.status === 'applied' ? 'Aplicada' : nc.status === 'cancelled' ? 'Cancelada' : 'Borrador'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono">
                              {nc.cfdi_uuid ? (
                                <span className="text-emerald-600">{nc.cfdi_uuid.substring(0, 8)}...</span>
                              ) : (
                                <span className="text-gray-400">Sin timbrar</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {/* Vista Previa PDF button */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setPreviewCreditNote(nc)}
                                  title="Vista Previa PDF"
                                >
                                  <Eye className="h-4 w-4 text-purple-500" />
                                </Button>
                                {/* Descargar XML button */}
                                {nc.cfdi_uuid && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDownloadCreditNoteXML(nc.id)}
                                    title="Descargar XML"
                                  >
                                    <FileText className="h-4 w-4 text-green-600" />
                                  </Button>
                                )}
                                {!nc.cfdi_uuid && nc.status !== 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => handleStampCreditNote(nc.id)}
                                    disabled={stampingCreditNote === nc.id}
                                  >
                                    {stampingCreditNote === nc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <FileCheck className="h-4 w-4" />
                                    )}
                                    <span className="ml-1 hidden sm:inline">Timbrar</span>
                                  </Button>
                                )}
                                {nc.cfdi_uuid && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={() => handleSendCreditNoteEmail(nc.id)}
                                    disabled={sendingCreditNoteEmail === nc.id}
                                  >
                                    {sendingCreditNoteEmail === nc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Mail className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                                {/* Cancelar CFDI - solo si está timbrada y no cancelada */}
                                {nc.cfdi_uuid && nc.cfdi_status !== 'cancelled' && nc.status !== 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    onClick={() => handleCancelCreditNoteCFDI(nc.id)}
                                    disabled={cancellingCreditNote === nc.id}
                                    title="Cancelar CFDI en SAT"
                                  >
                                    {cancellingCreditNote === nc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <FileX className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                                {/* Re-timbrar - solo si fue cancelada */}
                                {nc.cfdi_status === 'cancelled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => handleStampCreditNote(nc.id)}
                                    disabled={stampingCreditNote === nc.id}
                                    title="Re-timbrar nota cancelada"
                                  >
                                    {stampingCreditNote === nc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <FileCheck className="h-4 w-4" />
                                    )}
                                    <span className="ml-1 hidden sm:inline">Re-timbrar</span>
                                  </Button>
                                )}
                                {/* Eliminar - solo si no está timbrada o si fue cancelada */}
                                {(!nc.cfdi_uuid || nc.cfdi_status === 'cancelled') && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => handleDeleteCreditNote(nc.id)}
                                    disabled={deletingCreditNote === nc.id}
                                    title="Eliminar nota permanentemente"
                                  >
                                    {deletingCreditNote === nc.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments (Complementos de Pago) Tab */}
        <TabsContent value="payments" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-emerald-700">
                  <CreditCard className="h-5 w-5" />
                  Complementos de Pago
                </CardTitle>
                <CardDescription>Historial de pagos/abonos recibidos (listos para timbrar como complemento de pago)</CardDescription>
              </div>
              <Button 
                onClick={openMultiPaymentDialog}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Crear Pago
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Factura Origen</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Forma Pago SAT</TableHead>
                      <TableHead>Parcialidad</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead>Folio REP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          {searchFilter ? `No hay pagos que coincidan con "${searchFilter}"` : "No hay pagos registrados"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPayments.map((payment) => {
                        const invoice = invoices.find(i => i.id === payment.invoice_id);
                        const client = clients.find(c => c.id === payment.client_id);
                        const formaPago = SAT_FORMAS_PAGO.find(f => f.value === payment.sat_forma_pago);
                        return (
                          <TableRow key={payment.id}>
                            <TableCell>{formatDate(payment.payment_date)}</TableCell>
                            <TableCell className="font-medium">{invoice?.invoice_number || '-'}</TableCell>
                            <TableCell>{client?.name || '-'}</TableCell>
                            <TableCell className="text-xs">{formaPago?.label || payment.sat_forma_pago || '-'}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">#{payment.num_parcialidad || 1}</Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium text-emerald-600">
                              {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{payment.reference || '-'}</TableCell>
                            <TableCell>
                              {payment.cfdi_complemento_uuid ? (
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    variant={payment.cfdi_complemento_status === 'cancelled' ? 'destructive' : 'default'} 
                                    className="text-xs"
                                  >
                                    {payment.cfdi_complemento_status === 'cancelled' 
                                      ? 'Cancelado' 
                                      : payment.cfdi_complemento_status === 'cancellation_pending'
                                        ? 'Cancelación Pendiente'
                                        : payment.rep_folio || 'Timbrado'}
                                  </Badge>
                                  {payment.cfdi_complemento_status === 'cancelled' && (
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2"
                                        title="Descargar Acuse XML"
                                        onClick={() => handleDownloadPaymentCancellationAcuse(payment.id, payment.rep_folio, 'xml')}
                                      >
                                        <FileCode className="h-3 w-3 text-red-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2"
                                        title="Descargar Acuse PDF"
                                        onClick={() => handleDownloadPaymentCancellationAcuse(payment.id, payment.rep_folio, 'pdf')}
                                      >
                                        <FileText className="h-3 w-3 text-red-500" />
                                      </Button>
                                    </div>
                                  )}
                                  {payment.cfdi_complemento_status !== 'cancelled' && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2"
                                        title="Vista Previa PDF"
                                        onClick={() => setPreviewPayment(payment)}
                                      >
                                        <Eye className="h-3 w-3 text-purple-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2"
                                        title="Descargar XML"
                                        onClick={() => handleDownloadPaymentXML(payment.id)}
                                      >
                                        <Download className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2"
                                        title="Descargar PDF"
                                        onClick={() => handleDownloadPaymentPDF(payment.id)}
                                      >
                                        <FileText className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        title="Cancelar Complemento de Pago"
                                        onClick={() => handleCancelPaymentComplement(payment.id, payment.rep_folio)}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              ) : invoice?.metodo_pago === "PPD" && invoice?.cfdi_status === "stamped" ? (
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => handleStampPayment(payment.id)}
                                    data-testid={`stamp-rep-${payment.id}`}
                                  >
                                    Timbrar REP
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Eliminar pago"
                                    onClick={() => handleDeletePayment(payment.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Badge
                                    variant="secondary"
                                    className="text-[10px] font-normal"
                                    title={
                                      !invoice
                                        ? "No se encontró la factura origen de este pago"
                                        : invoice.metodo_pago !== "PPD"
                                          ? "La factura se emitió como PUE (pago en una sola exhibición): el SAT no pide complemento de pago. Si el cobro fue posterior, debía emitirse como PPD."
                                          : "La factura aún no está timbrada ante el SAT; primero tímbrala y después podrás timbrar el REP."
                                    }
                                    data-testid={`rep-reason-${payment.id}`}
                                  >
                                    {!invoice
                                      ? "Factura no encontrada"
                                      : invoice.metodo_pago !== "PPD"
                                        ? "PUE · no requiere REP"
                                        : "Factura sin timbrar"}
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Eliminar pago"
                                    onClick={() => handleDeletePayment(payment.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{selectedInvoice ? "Editar Factura" : "Nueva Factura"}</DialogTitle>
              <DialogDescription>
                {selectedInvoice 
                  ? "Modifica los datos de la factura (no disponible para facturas timbradas)" 
                  : "Registra una nueva factura con datos fiscales para CFDI"}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Folio</Label>
                  <Input
                    value={formData.invoice_number}
                    disabled
                    className="bg-gray-100"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Fecha de Factura *</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => {
                      const newInvoiceDate = e.target.value;
                      let newDueDate = formData.due_date;
                      
                      // Recalcular fecha de vencimiento si hay cliente seleccionado
                      if (formData.client_id && newInvoiceDate) {
                        const selectedClient = clients.find(c => c.id === formData.client_id);
                        const creditDays = selectedClient?.credit_days || 0;
                        if (creditDays > 0) {
                          const invoiceDate = new Date(newInvoiceDate);
                          invoiceDate.setDate(invoiceDate.getDate() + creditDays);
                          newDueDate = invoiceDate.toISOString().split('T')[0];
                        }
                      }
                      
                      setFormData({ ...formData, invoice_date: newInvoiceDate, due_date: newDueDate });
                    }}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Fecha Vencimiento</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Cliente * <span className="text-xs text-muted-foreground">(Solo clientes, no prospectos)</span></Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => {
                    const selectedClient = clients.find(c => c.id === value);
                    const creditDays = selectedClient?.credit_days || 0;
                    
                    // Calcular fecha de vencimiento basada en días de crédito del cliente
                    let newDueDate = formData.due_date;
                    let newPaymentTerms = formData.payment_terms || "contado";
                    
                    if (creditDays > 0 && formData.invoice_date) {
                      const invoiceDate = new Date(formData.invoice_date);
                      invoiceDate.setDate(invoiceDate.getDate() + creditDays);
                      newDueDate = invoiceDate.toISOString().split('T')[0];
                      
                      // Mapear días de crédito a payment_terms
                      if (creditDays <= 7) newPaymentTerms = "7_dias";
                      else if (creditDays <= 15) newPaymentTerms = "15_dias";
                      else if (creditDays <= 30) newPaymentTerms = "30_dias";
                      else if (creditDays <= 45) newPaymentTerms = "45_dias";
                      else if (creditDays <= 60) newPaymentTerms = "60_dias";
                      else if (creditDays <= 90) newPaymentTerms = "90_dias";
                      else newPaymentTerms = "90_dias";
                    }
                    
                    setFormData({ 
                      ...formData, 
                      client_id: value,
                      uso_cfdi: selectedClient?.uso_cfdi || formData.uso_cfdi || "G03",
                      payment_terms: newPaymentTerms,
                      due_date: newDueDate
                    });
                  }}
                >
                  <SelectTrigger className="truncate"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => !c.is_prospect).length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        No hay clientes disponibles. Convierte un prospecto a cliente desde el CRM.
                      </div>
                    ) : (
                      clients.filter(c => !c.is_prospect).map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="block whitespace-normal">
                            {c.trade_name || c.name} {c.rfc && `- ${c.rfc}`}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Proyecto (opcional)</Label>
                <Select
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar proyecto" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Referencia (OC Cliente / Orden de Trabajo)</Label>
                  <Input
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    placeholder="Ej: OC-2024-001, OT-123"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Condiciones de Pago</Label>
                  <Select
                    value={formData.payment_terms || "contado"}
                    onValueChange={(value) => {
                      // Calcular nueva fecha de vencimiento basada en términos de pago
                      let newDueDate = formData.due_date;
                      const daysMap = {
                        "contado": 0,
                        "7_dias": 7,
                        "15_dias": 15,
                        "30_dias": 30,
                        "45_dias": 45,
                        "60_dias": 60,
                        "90_dias": 90
                      };
                      
                      const days = daysMap[value] || 0;
                      if (formData.invoice_date && days > 0) {
                        const invoiceDate = new Date(formData.invoice_date);
                        invoiceDate.setDate(invoiceDate.getDate() + days);
                        newDueDate = invoiceDate.toISOString().split('T')[0];
                      } else if (days === 0) {
                        newDueDate = formData.invoice_date || "";
                      }
                      
                      setFormData({ ...formData, payment_terms: value, due_date: newDueDate });
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Forma de Pago SAT</Label>
                  <Select
                    value={formData.forma_pago || "99"}
                    onValueChange={(value) => setFormData({ ...formData, forma_pago: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SAT_FORMAS_PAGO.map(fp => (
                        <SelectItem key={fp.value} value={fp.value}>{fp.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Método de Pago</Label>
                  <Select
                    value={formData.metodo_pago || "PUE"}
                    onValueChange={(value) => setFormData({ ...formData, metodo_pago: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {METODO_PAGO_OPTIONS.map(mp => (
                        <SelectItem key={mp.value} value={mp.value}>{mp.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Uso CFDI</Label>
                  <Select
                    value={formData.uso_cfdi || "G03"}
                    onValueChange={(value) => setFormData({ ...formData, uso_cfdi: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {USO_CFDI_OPTIONS.map(uc => (
                        <SelectItem key={uc.value} value={uc.value}>{uc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid gap-2">
                <Label>{formData.custom_field_label || "Número de Orden / Referencia Extra"}</Label>
                <Input
                  value={formData.custom_field || ""}
                  onChange={(e) => setFormData({ ...formData, custom_field: e.target.value })}
                  placeholder="Ej: ORD-001, PO-12345"
                />
              </div>

              {/* Items Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Conceptos / Partidas</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <PlusCircle className="mr-1 h-4 w-4" />
                    Agregar
                  </Button>
                </div>
                {formData.items.map((item, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-3 border">
                    <div>
                      <Label className="text-xs">Descripción *</Label>
                      <ItemDescriptionField
                        value={item.description}
                        onValueChange={(val) => handleItemChange(index, "description", val)}
                        placeholder="Descripción del servicio/producto"
                        required
                        data-testid={`invoice-item-description-${index}`}
                      />
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label className="text-xs">Unidad</Label>
                        <Input
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                        />
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs">P. Unitario</Label>
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, "unit_price", parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-4 flex items-end gap-1">
                        <div className="flex-1">
                          <Label className="text-xs">Total</Label>
                          <Input value={formatCurrency(item.quantity * item.unit_price)} disabled className="text-xs" />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          className="h-9 w-9"
                        >
                          <MinusCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {/* SAT Keys */}
                    <div className="flex flex-wrap gap-3 items-start border-t pt-3 mt-2">
                      <div className="flex-1 min-w-[180px] max-w-[250px]">
                        <Label className="text-xs text-blue-600 mb-1 block">Clave SAT Producto/Servicio</Label>
                        <SATProductSearch
                          value={item.clave_prod_serv || ""}
                          onChange={(val) => handleItemChange(index, "clave_prod_serv", val)}
                          placeholder="Buscar clave SAT..."
                        />
                        {!item.clave_prod_serv && (
                          <p className="text-[10px] text-slate-400 mt-1">Ej: 01010101</p>
                        )}
                      </div>
                      <div className="flex-1 min-w-[150px] max-w-[200px]">
                        <Label className="text-xs text-blue-600 mb-1 block">Clave Unidad SAT</Label>
                        <SATUnitSearch
                          value={item.clave_unidad || ""}
                          onChange={(val) => handleItemChange(index, "clave_unidad", val)}
                        />
                        {!item.clave_unidad && (
                          <p className="text-[10px] text-slate-400 mt-1">Ej: H87</p>
                        )}
                      </div>
                      <div className="flex items-center text-[10px] text-slate-400 pt-5">
                        Requeridos para CFDI
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 p-4 bg-slate-100 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="font-medium">{formatCurrency(formData.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>IVA (16%):</span>
                  <span className="font-medium">{formatCurrency(formData.tax)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-primary">{formatCurrency(formData.total)}</span>
                </div>
              </div>
              
              {/* Campo Personalizado - Debajo de totales */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="grid gap-2">
                  <Label className="text-blue-700">Etiqueta del Campo Personalizado</Label>
                  <Input
                    value={formData.custom_field_label}
                    onChange={(e) => setFormData({ ...formData, custom_field_label: e.target.value })}
                    placeholder="Ej: Orden de Trabajo, Pedido, Contrato"
                    className="bg-white"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-blue-700">{formData.custom_field_label || "Campo Personalizado"}</Label>
                  <Input
                    value={formData.custom_field}
                    onChange={(e) => setFormData({ ...formData, custom_field: e.target.value })}
                    placeholder={formData.custom_field_label ? `Ingrese ${formData.custom_field_label}` : "Valor del campo personalizado"}
                    className="bg-white"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-industrial">Registrar Factura</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog - Complemento de Pago CFDI */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handlePayment}>
            <DialogHeader>
              <DialogTitle>Registrar Abono / Complemento de Pago</DialogTitle>
              <DialogDescription className="space-y-1">
                <div>
                  Factura: <strong>{selectedInvoice?.invoice_number}</strong> | 
                  Total: {formatCurrency(selectedInvoice?.total || 0)} |
                  Pagado: {formatCurrency(selectedInvoice?.paid_amount || 0)} |
                  <span className="text-primary font-semibold"> Saldo: {formatCurrency((selectedInvoice?.total || 0) - (selectedInvoice?.paid_amount || 0))}</span>
                </div>
                <div className="text-xs">
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    Parcialidad #{paymentForm.num_parcialidad}
                  </span>
                  {selectedInvoice && payments.filter(p => p.invoice_id === selectedInvoice.id).length > 0 && (
                    <span className="ml-2 text-slate-500">
                      ({payments.filter(p => p.invoice_id === selectedInvoice.id).length} pago(s) previo(s))
                    </span>
                  )}
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Sección: Datos del Pago */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Datos del Pago
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Monto del Abono *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      className="h-9"
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Fecha de Pago *</Label>
                    <Input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      required
                      className="h-9"
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">No. Parcialidad</Label>
                    <Input
                      type="number"
                      min="1"
                      value={paymentForm.num_parcialidad}
                      onChange={(e) => setPaymentForm({ ...paymentForm, num_parcialidad: e.target.value })}
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Forma de Pago SAT *</Label>
                    <Select
                      value={paymentForm.sat_forma_pago}
                      onValueChange={(value) => setPaymentForm({ ...paymentForm, sat_forma_pago: value })}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SAT_FORMAS_PAGO.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Número de Operación</Label>
                    <Input
                      value={paymentForm.num_operacion}
                      onChange={(e) => setPaymentForm({ ...paymentForm, num_operacion: e.target.value })}
                      placeholder="No. transferencia, autorización, etc."
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Moneda del Pago</Label>
                    <Select
                      value={paymentForm.moneda_pago}
                      onValueChange={(value) => setPaymentForm({ ...paymentForm, moneda_pago: value })}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONEDAS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Tipo de Cambio</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={paymentForm.tipo_cambio}
                      onChange={(e) => setPaymentForm({ ...paymentForm, tipo_cambio: e.target.value })}
                      placeholder="1.0000"
                      disabled={paymentForm.moneda_pago === "MXN"}
                      className="h-9"
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Método (interno)</Label>
                    <Select
                      value={paymentForm.payment_method}
                      onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Sección: Datos Bancarios */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Datos Bancarios <span className="text-xs font-normal text-slate-400">(Opcionales para CFDI)</span>
                </h4>
                
                {/* Banco Ordenante (quien paga) */}
                <div className="p-3 bg-slate-50 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-slate-600">Banco Ordenante (quien paga) - <span className="text-amber-600">Opcional</span></p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1">
                      <Label className="text-xs">Banco</Label>
                      <select
                        value={paymentForm.rfc_banco_ordenante}
                        onChange={(e) => {
                          const banco = bancos.find(b => b.rfc === e.target.value);
                          setPaymentForm({ 
                            ...paymentForm, 
                            rfc_banco_ordenante: e.target.value,
                            nombre_banco_ordenante: banco?.nombre || ""
                          });
                        }}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2"
                      >
                        <option value="">-- No especificar (recomendado) --</option>
                        {bancos.filter(b => b.rfc).map((banco) => (
                          <option key={banco.clave} value={banco.rfc}>
                            {banco.nombre} ({banco.rfc})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Cuenta Ordenante (CLABE o No. Cuenta)</Label>
                      <Input
                        value={paymentForm.cuenta_ordenante}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cuenta_ordenante: e.target.value.replace(/\D/g, '') })}
                        placeholder="Opcional - 10, 16 o 18 dígitos"
                        maxLength={18}
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-600">⚠️ Si no estás seguro del banco, déjalo vacío. Facturama validará el RFC contra el SAT.</p>
                </div>

                {/* Banco Beneficiario (quien recibe) */}
                <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-blue-700">Banco Beneficiario (quien recibe) - <span className="text-amber-600">Opcional</span></p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-1">
                      <Label className="text-xs">Banco</Label>
                      <select
                        value={paymentForm.rfc_banco_beneficiario}
                        onChange={(e) => setPaymentForm({ ...paymentForm, rfc_banco_beneficiario: e.target.value })}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2"
                      >
                        <option value="">-- No especificar (recomendado) --</option>
                        {bancos.filter(b => b.rfc).map((banco) => (
                          <option key={banco.clave} value={banco.rfc}>
                            {banco.nombre} ({banco.rfc})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-1">
                      <Label className="text-xs">Cuenta Beneficiaria (CLABE o No. Cuenta)</Label>
                      <Input
                        value={paymentForm.cuenta_beneficiaria}
                        onChange={(e) => setPaymentForm({ ...paymentForm, cuenta_beneficiaria: e.target.value.replace(/\D/g, '') })}
                        placeholder="Opcional - 10, 16 o 18 dígitos"
                        maxLength={18}
                        className="h-8 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Sección: Comprobante y Notas */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Comprobante y Notas
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Comprobante de Pago</Label>
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setPaymentForm({ ...paymentForm, proof_file: e.target.files?.[0] })}
                      className="h-9 text-xs"
                    />
                    <p className="text-[10px] text-muted-foreground">PDF o imagen del comprobante bancario</p>
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Referencia</Label>
                    <Input
                      value={paymentForm.reference}
                      onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                      placeholder="No. de cheque, folio, etc."
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Notas / Observaciones</Label>
                  <Textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Observaciones adicionales del pago..."
                    rows={2}
                    className="text-xs"
                  />
                </div>
              </div>

              {/* Resumen del documento relacionado */}
              {selectedInvoice?.sat_invoice_uuid && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs font-medium text-green-700 mb-2">Documento Relacionado (CFDI)</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-slate-500">UUID:</span> <span className="font-mono">{selectedInvoice.sat_invoice_uuid}</span></div>
                    <div><span className="text-slate-500">Folio:</span> {selectedInvoice.invoice_number}</div>
                    <div><span className="text-slate-500">Total Factura:</span> {formatCurrency(selectedInvoice.total)}</div>
                    <div><span className="text-slate-500">Saldo Anterior:</span> {formatCurrency((selectedInvoice.total || 0) - (selectedInvoice.paid_amount || 0))}</div>
                  </div>
                </div>
              )}

              {/* Info para CFDI */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Nota:</strong> Los datos bancarios son opcionales pero recomendados para el complemento de pago CFDI 4.0. 
                  El saldo insoluto se calculará automáticamente al registrar el pago.
                </p>
              </div>

              {/* Checkbox para timbrar complemento - solo si la factura es PPD y está timbrada */}
              {selectedInvoice?.metodo_pago === "PPD" && selectedInvoice?.cfdi_status === "stamped" && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paymentForm.timbrar_complemento}
                      onChange={(e) => setPaymentForm({ ...paymentForm, timbrar_complemento: e.target.checked })}
                      className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Timbrar Complemento de Pago (REP)</p>
                      <p className="text-xs text-blue-600">
                        Emitir CFDI de tipo Pago automáticamente al registrar este abono
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-industrial" disabled={stampingPayment}>
                {stampingPayment ? "Timbrando..." : "Registrar Abono"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Statement Dialog */}
      <Dialog open={statementDialogOpen} onOpenChange={setStatementDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Estado de Cuenta</DialogTitle>
            <DialogDescription>
              {clientStatement?.client?.name}
            </DialogDescription>
          </DialogHeader>
          {clientStatement && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">Total Facturado</p>
                    <p className="text-xl font-bold">{formatCurrency(clientStatement.summary.total_invoiced)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">Total Pagado</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(clientStatement.summary.total_paid)}</p>
                  </CardContent>
                </Card>
                <Card className={clientStatement.summary.balance > 0 ? "border-amber-200 bg-amber-50" : ""}>
                  <CardContent className="p-3 text-center">
                    <p className="text-sm text-muted-foreground">Saldo Pendiente</p>
                    <p className={`text-xl font-bold ${clientStatement.summary.balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                      {formatCurrency(clientStatement.summary.balance)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Overdue Warning */}
              {clientStatement.summary.overdue_count > 0 && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-red-800 text-sm">
                      {clientStatement.summary.overdue_count} factura(s) vencida(s) por {formatCurrency(clientStatement.summary.overdue_amount)}
                    </span>
                  </CardContent>
                </Card>
              )}

              {/* Invoices List - Only show invoices with pending balance */}
              <div>
                <h4 className="font-semibold mb-2">Facturas con Saldo Pendiente</h4>
                <div className="border rounded-sm max-h-40 overflow-y-auto">
                  {clientStatement.invoices
                    .filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled' && (inv.total - inv.paid_amount) > 0)
                    .map((inv) => (
                    <div key={inv.id} className="p-2 border-b last:border-b-0 flex items-center justify-between text-sm">
                      <div>
                        <span className="font-mono">{inv.invoice_number}</span>
                        <span className="text-muted-foreground ml-2 truncate max-w-[150px]">{inv.concept}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span>{formatCurrency(inv.total - inv.paid_amount)}</span>
                        <Badge className={getStatusColor(inv.status)}>{getStatusLabel(inv.status)}</Badge>
                      </div>
                    </div>
                  ))}
                  {clientStatement.invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled' && (inv.total - inv.paid_amount) > 0).length === 0 && (
                    <div className="p-4 text-center text-sm text-emerald-600">
                      ✓ No hay facturas con saldo pendiente
                    </div>
                  )}
                </div>
              </div>

              {/* Payments List */}
              {clientStatement.payments.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Pagos Recibidos</h4>
                  <div className="border rounded-sm max-h-40 overflow-y-auto">
                    {clientStatement.payments.map((p) => (
                      <div key={p.id} className="p-2 border-b last:border-b-0 flex items-center justify-between text-sm">
                        <div>
                          <span>{formatDate(p.payment_date)}</span>
                          <span className="text-muted-foreground ml-2">{p.payment_method}</span>
                          {p.reference && <span className="text-muted-foreground ml-1">({p.reference})</span>}
                        </div>
                        <span className="text-emerald-600 font-medium">{formatCurrency(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {clientStatement && (
              <Button 
                variant="outline" 
                onClick={() => handleDownloadStatementPDF(clientStatement.client.id)}
                className="mr-auto"
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => setStatementDialogOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reference Edit Dialog - For Stamped Invoices */}
      <Dialog open={referenceDialogOpen} onOpenChange={setReferenceDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSaveReference}>
            <DialogHeader>
              <DialogTitle>Editar Referencia / OC</DialogTitle>
              <DialogDescription>
                Factura: <strong>{selectedInvoice?.invoice_number}</strong> - 
                Modifica los campos de referencia sin afectar el CFDI timbrado.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Referencia (OC Cliente / Orden de Trabajo)</Label>
                <Input
                  value={referenceForm.reference}
                  onChange={(e) => setReferenceForm({ ...referenceForm, reference: e.target.value })}
                  placeholder="Ej: OC-2024-001, OT-123"
                />
              </div>
              <div className="grid gap-2">
                <Label>Etiqueta del Campo Personalizado</Label>
                <Input
                  value={referenceForm.custom_field_label}
                  onChange={(e) => setReferenceForm({ ...referenceForm, custom_field_label: e.target.value })}
                  placeholder="Ej: NUMERO DE PROVEEDOR, Orden de Trabajo"
                />
              </div>
              <div className="grid gap-2">
                <Label>{referenceForm.custom_field_label || "Campo Personalizado"}</Label>
                <Input
                  value={referenceForm.custom_field}
                  onChange={(e) => setReferenceForm({ ...referenceForm, custom_field: e.target.value })}
                  placeholder="Valor del campo personalizado"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReferenceDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-industrial">Guardar Referencia</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credit Note Dialog */}
      <Dialog open={creditNoteDialogOpen} onOpenChange={setCreditNoteDialogOpen}>
        <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleCreditNoteSubmit}>
            <DialogHeader>
              <DialogTitle className="text-violet-700">Nueva Nota de Crédito</DialogTitle>
              <DialogDescription>
                CFDI tipo "E" (Egreso) - Factura relacionada: <strong>{selectedInvoice?.invoice_number}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Header Info */}
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1">
                  <Label className="text-xs">Folio Nota de Crédito</Label>
                  <Input
                    value={creditNoteForm.credit_note_number}
                    onChange={(e) => setCreditNoteForm({ ...creditNoteForm, credit_note_number: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Fecha de Emisión *</Label>
                  <Input
                    type="date"
                    value={creditNoteForm.issue_date}
                    onChange={(e) => setCreditNoteForm({ ...creditNoteForm, issue_date: e.target.value })}
                    className="h-9"
                    required
                  />
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Tipo Relación SAT</Label>
                  <Select
                    value={creditNoteForm.sat_tipo_relacion}
                    onValueChange={(value) => setCreditNoteForm({ ...creditNoteForm, sat_tipo_relacion: value })}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="01">01 - Nota de crédito</SelectItem>
                      <SelectItem value="03">03 - Devolución de mercancía</SelectItem>
                      <SelectItem value="04">04 - Sustitución de CFDI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Related Invoice Info */}
              {selectedInvoice && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs font-medium text-slate-600 mb-2">Factura Relacionada</p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div><span className="text-slate-400">Folio:</span> <span className="font-medium">{selectedInvoice.invoice_number}</span></div>
                    <div><span className="text-slate-400">Total:</span> <span className="font-medium">{formatCurrency(selectedInvoice.total)}</span></div>
                    <div><span className="text-slate-400">Pagado:</span> <span className="font-medium">{formatCurrency(selectedInvoice.paid_amount)}</span></div>
                    <div><span className="text-slate-400">Saldo:</span> <span className="font-medium text-amber-600">{formatCurrency((selectedInvoice.total || 0) - (selectedInvoice.paid_amount || 0))}</span></div>
                  </div>
                  {selectedInvoice.sat_invoice_uuid && (
                    <p className="text-xs mt-2 text-slate-500">
                      UUID: <span className="font-mono">{selectedInvoice.sat_invoice_uuid}</span>
                    </p>
                  )}
                </div>
              )}

              {/* Reason */}
              <div className="grid gap-1">
                <Label className="text-xs">Motivo de la Nota de Crédito *</Label>
                <Select
                  value={creditNoteForm.reason}
                  onValueChange={(value) => setCreditNoteForm({ ...creditNoteForm, reason: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Seleccionar motivo" /></SelectTrigger>
                  <SelectContent>
                    {motivosNC.map((motivo, idx) => (
                      <SelectItem key={idx} value={motivo}>{motivo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Conceptos</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addCreditNoteItem}>
                    <PlusCircle className="mr-1 h-4 w-4" />
                    Agregar
                  </Button>
                </div>
                {creditNoteForm.items.map((item, index) => (
                  <div key={index} className="p-3 bg-violet-50 rounded-lg space-y-2 border border-violet-200">
                    <div>
                      <Label className="text-xs">Descripción</Label>
                      <ItemDescriptionField
                        value={item.description}
                        onValueChange={(val) => handleCreditNoteItemChange(index, "description", val)}
                        placeholder="Concepto del crédito"
                        data-testid={`credit-note-item-description-${index}`}
                      />
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleCreditNoteItemChange(index, "quantity", parseFloat(e.target.value) || 0)}
                          className="h-8"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-3">
                        <Label className="text-xs">P. Unitario</Label>
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleCreditNoteItemChange(index, "unit_price", parseFloat(e.target.value) || 0)}
                          className="h-8"
                        />
                      </div>
                      <div className="col-span-4 md:col-span-4">
                        <Label className="text-xs">Total</Label>
                        <Input value={formatCurrency(item.quantity * item.unit_price)} disabled className="h-8 text-xs" />
                      </div>
                      <div className="col-span-12 md:col-span-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeCreditNoteItem(index)}
                          disabled={creditNoteForm.items.length === 1}
                          className="h-8 w-8"
                        >
                          <MinusCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {/* SAT Keys */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="grid gap-1">
                        <Label className="text-xs text-violet-700">Clave SAT Producto</Label>
                        <SATProductSearch
                          value={item.clave_prod_serv || ""}
                          onChange={(val) => handleCreditNoteItemChange(index, "clave_prod_serv", val)}
                          placeholder="Buscar clave..."
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs text-violet-700">Clave Unidad SAT</Label>
                        <SATUnitSearch
                          value={item.clave_unidad || ""}
                          onChange={(val) => handleCreditNoteItemChange(index, "clave_unidad", val)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 p-4 bg-violet-100 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Subtotal:</span>
                  <span className="font-medium">{formatCurrency(creditNoteForm.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>IVA (16%):</span>
                  <span className="font-medium">{formatCurrency(creditNoteForm.tax)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-violet-700">
                  <span>Total Nota de Crédito:</span>
                  <span>{formatCurrency(creditNoteForm.total)}</span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="apply_immediately"
                  checked={creditNoteForm.status === "applied"}
                  onChange={(e) => setCreditNoteForm({ ...creditNoteForm, status: e.target.checked ? "applied" : "draft" })}
                  className="h-4 w-4"
                />
                <Label htmlFor="apply_immediately" className="text-sm">
                  Aplicar inmediatamente al saldo de la factura
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreditNoteDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700">Registrar Nota de Crédito</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CFDI Manual Upload Dialog */}
      <Dialog open={cfdiDialogOpen} onOpenChange={setCfdiDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleUploadManualCFDI}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-orange-500" />
                Subir CFDI Manual
              </DialogTitle>
              <DialogDescription>
                Sube el CFDI generado externamente para vincularlo a esta factura.
                El UUID se extraerá automáticamente del XML.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Archivo XML *</Label>
                <Input
                  type="file"
                  accept=".xml"
                  required
                  onChange={(e) => setCfdiForm({ ...cfdiForm, xml_file: e.target.files?.[0] })}
                />
                <p className="text-xs text-muted-foreground">
                  El XML debe contener el TimbreFiscalDigital con el UUID
                </p>
              </div>
              <div className="space-y-2">
                <Label>Archivo PDF *</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  required
                  onChange={(e) => setCfdiForm({ ...cfdiForm, pdf_file: e.target.files?.[0] })}
                />
              </div>
              <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                ⚠️ Ambos archivos son obligatorios. El UUID se extraerá automáticamente del XML.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCfdiDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                Vincular CFDI
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar CFDIs</DialogTitle>
            <DialogDescription>
              Selecciona el rango de fechas y el formato de exportación
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={exportForm.start_date}
                  onChange={(e) => setExportForm({...exportForm, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha Fin</Label>
                <Input
                  type="date"
                  value={exportForm.end_date}
                  onChange={(e) => setExportForm({...exportForm, end_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Formato</Label>
              <Select value={exportForm.format} onValueChange={(v) => setExportForm({...exportForm, format: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">Excel (Detallado con formato)</SelectItem>
                  <SelectItem value="aspel">ASPEL COI/SAE (TXT delimitado)</SelectItem>
                  <SelectItem value="contpaqi">CONTPAQi (CSV)</SelectItem>
                  <SelectItem value="xml">XMLs originales (ZIP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Rango máximo: 1 año. Los archivos incluyen solo CFDIs timbrados o cancelados.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              className="btn-industrial" 
              onClick={() => handleExport(exportForm.format)}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Descargar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modals */}
      <PDFPreviewModal
        open={!!previewInvoice}
        onClose={() => setPreviewInvoice(null)}
        documentType="invoice"
        documentId={previewInvoice?.id}
        documentNumber={previewInvoice?.invoice_number}
        token={localStorage.getItem("cia_token")}
      />

      <PDFPreviewModal
        open={!!previewCreditNote}
        onClose={() => setPreviewCreditNote(null)}
        documentType="credit-note"
        documentId={previewCreditNote?.id}
        documentNumber={previewCreditNote?.credit_note_number}
        token={localStorage.getItem("cia_token")}
      />

      <PDFPreviewModal
        open={!!previewPayment}
        onClose={() => setPreviewPayment(null)}
        documentType="payment-complement"
        documentId={previewPayment?.id}
        documentNumber={previewPayment?.rep_folio || previewPayment?.cfdi_complemento_uuid?.substring(0, 8)}
        token={localStorage.getItem("cia_token")}
      />

      {/* Modal Crear Pago Multi-Factura */}
      <Dialog open={multiPaymentDialogOpen} onOpenChange={setMultiPaymentDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CreditCard className="h-5 w-5" />
              {createdMultiPayments && createdMultiPayments.payments ? "Pago Creado - Timbrar Complemento" : "Crear Pago Multi-Factura"}
            </DialogTitle>
            <DialogDescription>
              {createdMultiPayments && createdMultiPayments.payments
                ? "El pago se registró. Ahora puedes timbrar el complemento de pago."
                : "Registra un pago y distribúyelo entre varias facturas del mismo cliente"}
            </DialogDescription>
          </DialogHeader>

          {createdMultiPayments && createdMultiPayments.payments ? (
            // Vista después de crear el pago
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg">
                <h4 className="font-semibold text-emerald-800 mb-2">Distribución del Pago:</h4>
                <div className="space-y-2">
                  {createdMultiPayments.payments.map((p, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white p-2 rounded border">
                      <span className="text-sm">{p.invoice_number}</span>
                      <span className="font-medium">${p.amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                      <Badge variant="outline">Nuevo saldo: ${p.new_balance.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Badge>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-sm text-emerald-700">
                    <strong>Total:</strong> ${createdMultiPayments.payments.reduce((sum, p) => sum + p.amount, 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    Se generará UN solo CFDI Complemento de Pago (REP) que cubre las {createdMultiPayments.payments.length} factura(s).
                  </p>
                </div>
              </div>
              
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setMultiPaymentDialogOpen(false)}>
                  Cerrar
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleStampMultiPayments}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Timbrar Complemento Consolidado
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Formulario de creación
            <div className="space-y-4">
              {/* Selección de Cliente */}
              <div className="grid gap-2">
                <Label className="font-medium">Cliente *</Label>
                <select
                  value={multiPaymentForm.client_id}
                  onChange={(e) => handleMultiPaymentClientChange(e.target.value)}
                  className="h-10 rounded-md border border-input bg-background px-3"
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.trade_name || client.name} {client.rfc ? `(${client.rfc})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Facturas Pendientes del Cliente */}
              {selectedClientInvoices.length > 0 && (
                <div className="border rounded-lg p-3 bg-slate-50">
                  <Label className="font-medium mb-2 block">Facturas Pendientes de Pago</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedClientInvoices.map((inv) => {
                      const realBalance = inv.total - (inv.paid_amount || 0);
                      const paidWithoutRecord = realBalance <= 0.01;
                      const balance = paidWithoutRecord ? inv.total : realBalance;
                      const paymentItem = multiPaymentForm.invoice_payments.find(ip => ip.invoice_id === inv.id);
                      return (
                        <div key={inv.id} className={`flex items-center gap-3 p-2 rounded border ${paidWithoutRecord ? 'bg-amber-50 border-amber-300' : 'bg-white'}`} data-testid={`multi-payment-invoice-${inv.invoice_number}`}>
                          <div className="flex-1">
                            <div className="font-medium text-sm flex items-center gap-2">
                              {inv.invoice_number}
                              {paidWithoutRecord && (
                                <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                  PAGADA SIN COMPLEMENTO
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Total: ${inv.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })} | 
                              {paidWithoutRecord ? (
                                <span className="text-amber-700 font-medium"> Pago cobrado sin registro: regulariza para timbrar su REP</span>
                              ) : (
                                <> Saldo: <span className="text-amber-600 font-medium">${balance.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></>
                              )}
                            </div>
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max={balance}
                              placeholder="$0.00"
                              value={paymentItem?.amount || ""}
                              onChange={(e) => handleInvoiceAmountChange(inv.id, e.target.value)}
                              className="h-8 text-sm text-right"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Resumen de distribución */}
                  <div className="mt-3 pt-2 border-t flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total distribuido:</span>
                    <span className={`font-medium ${
                      Math.abs(getMultiPaymentDistributedTotal() - (parseFloat(multiPaymentForm.total_amount) || 0)) > 0.01
                        ? 'text-red-600'
                        : 'text-emerald-600'
                    }`}>
                      ${getMultiPaymentDistributedTotal().toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}

              {multiPaymentForm.client_id && selectedClientInvoices.length === 0 && (
                <div className="text-center py-4 text-muted-foreground bg-slate-50 rounded-lg">
                  Este cliente no tiene facturas PPD pendientes de pago
                </div>
              )}
              {/* Datos del Pago */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Monto Total del Pago *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="$0.00"
                    value={multiPaymentForm.total_amount}
                    onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, total_amount: e.target.value })}
                    className="text-right font-medium"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Fecha de Pago *</Label>
                  <Input
                    type="date"
                    value={multiPaymentForm.payment_date}
                    onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, payment_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Forma de Pago SAT *</Label>
                  <select
                    value={multiPaymentForm.sat_forma_pago}
                    onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, sat_forma_pago: e.target.value })}
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    {SAT_FORMAS_PAGO.map((fp) => (
                      <option key={fp.value} value={fp.value}>{fp.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid gap-2">
                  <Label>Número de Operación / Referencia</Label>
                  <Input
                    value={multiPaymentForm.num_operacion}
                    onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, num_operacion: e.target.value })}
                    placeholder="No. transferencia, cheque, etc."
                  />
                </div>
              </div>

              {/* Datos Bancarios (Opcionales) */}
              <details className="border rounded-lg p-3">
                <summary className="cursor-pointer font-medium text-sm">
                  Datos Bancarios (Opcional)
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="p-2 bg-slate-50 rounded">
                    <p className="text-xs font-medium text-slate-600 mb-2">Banco Ordenante (quien paga)</p>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={multiPaymentForm.rfc_banco_ordenante}
                        onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, rfc_banco_ordenante: e.target.value })}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2"
                      >
                        <option value="">-- No especificar --</option>
                        {bancos.filter(b => b.rfc).map((banco) => (
                          <option key={banco.clave} value={banco.rfc}>{banco.nombre}</option>
                        ))}
                      </select>
                      <Input
                        value={multiPaymentForm.cuenta_ordenante}
                        onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, cuenta_ordenante: e.target.value.replace(/\D/g, '') })}
                        placeholder="Cuenta (10, 16 o 18 dígitos)"
                        maxLength={18}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  <div className="p-2 bg-blue-50 rounded">
                    <p className="text-xs font-medium text-blue-700 mb-2">Banco Beneficiario (quien recibe)</p>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={multiPaymentForm.rfc_banco_beneficiario}
                        onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, rfc_banco_beneficiario: e.target.value })}
                        className="h-8 text-xs rounded-md border border-input bg-background px-2"
                      >
                        <option value="">-- No especificar --</option>
                        {bancos.filter(b => b.rfc).map((banco) => (
                          <option key={banco.clave} value={banco.rfc}>{banco.nombre}</option>
                        ))}
                      </select>
                      <Input
                        value={multiPaymentForm.cuenta_beneficiaria}
                        onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, cuenta_beneficiaria: e.target.value.replace(/\D/g, '') })}
                        placeholder="Cuenta (10, 16 o 18 dígitos)"
                        maxLength={18}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>
              </details>

              <div className="grid gap-2">
                <Label>Notas (Opcional)</Label>
                <Input
                  value={multiPaymentForm.notes}
                  onChange={(e) => setMultiPaymentForm({ ...multiPaymentForm, notes: e.target.value })}
                  placeholder="Notas adicionales del pago"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setMultiPaymentDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleCreateMultiPayment}
                  disabled={!multiPaymentForm.client_id || !multiPaymentForm.total_amount || selectedClientInvoices.length === 0}
                >
                  <Check className="h-4 w-4 mr-2" />
                  Crear Pago
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
